// web-frontend/src/tutorial/storage.js
// Per-user tutorial completion tracking (localStorage).
// Key shape: dsk_tutorial_done_{userId} -> { version, completedAt, skipped }
// Version is bumped when the step structure changes meaningfully so we can
// re-trigger the tutorial for users who already saw an older version.

const KEY_PREFIX = 'dsk_tutorial_done_';
const SESSION_STEP_KEY = 'dsk_tutorial_step_index';

// Bump this when the step list structure changes and existing users should
// see the new tutorial again. Bumping invalidates the per-user
// `dsk_tutorial_done_{userId}` flag so the tutorial auto-starts on next visit.
//
// Convention going forward: every time the tutorial gets a meaningful update
// (new steps, reordering, copy that changes the user's takeaway), bump this
// number and remind the user to deploy. Cosmetic-only changes can stay on
// the same version.
//
//   v1 (2026-05-06) — initial release
//   v2 (2026-05-06) — comprehensive tour expansion + Done button fix +
//                     design-system token alignment + step list panel
//   v3 (2026-05-06) — language picker as first step (4-language selection
//                     persisted to user.preferred_language + appStore +
//                     i18nService — fixes tutorial/page language drift)
//   v4 (2026-05-06) — session step pointer is now keyed to userId so that
//                     switching accounts in the same browser session no longer
//                     skips early steps (was hiding the language picker).
//                     Bumped to ensure every existing user sees v4 fresh.
//   v5 (2026-05-07) — 행사명 'DSK 2026' → 'AI Power +' 변경 + 매칭 단계 안내
//                     문구 강화 + 클릭 대상 sparkle/glow + Country&City 다국어
//                     렌더링. 사용자 요청에 따라 모든 계정의 튜토리얼 완료
//                     플래그를 무효화 (튜토리얼 상태만 초기화 — 언어/북마크/
//                     기타 사용자 데이터는 일체 건드리지 않음).
export const TUTORIAL_VERSION = 5;

function buildKey(userId) {
  return KEY_PREFIX + String(userId || 'anonymous');
}

export function isDone(userId) {
  try {
    const raw = localStorage.getItem(buildKey(userId));
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return false;
    return parsed.version === TUTORIAL_VERSION;
  } catch (_e) {
    return false;
  }
}

export function markDone(userId, { skipped = false } = {}) {
  try {
    const payload = {
      version: TUTORIAL_VERSION,
      completedAt: new Date().toISOString(),
      skipped: !!skipped,
    };
    localStorage.setItem(buildKey(userId), JSON.stringify(payload));
  } catch (_e) {
    // localStorage quota or unavailable - silently ignore.
  }
}

export function reset(userId) {
  try {
    localStorage.removeItem(buildKey(userId));
    sessionStorage.removeItem(SESSION_STEP_KEY);
  } catch (_e) {
    // ignore
  }
}

// Session-scoped step pointer. Stored as a JSON envelope keyed to the userId
// so that switching accounts in the same browser session does NOT make the
// new user resume the previous user's progress (which was the bug that hid
// the language picker — a previous user's step_index leaked across logins).
export function loadSessionStepIndex(userId) {
  try {
    const v = sessionStorage.getItem(SESSION_STEP_KEY);
    if (v == null) return null;
    // New format: JSON envelope { userId, stepIndex }
    if (v.startsWith('{')) {
      const obj = JSON.parse(v);
      if (!obj || typeof obj !== 'object') return null;
      if (String(obj.userId || '') !== String(userId || '')) return null;
      const n = parseInt(obj.stepIndex, 10);
      return Number.isFinite(n) ? n : null;
    }
    // Legacy format: bare integer. Treat as stale (don't resume) since we
    // can't tell which user it belonged to.
    return null;
  } catch (_e) {
    return null;
  }
}

export function saveSessionStepIndex(index, userId) {
  try {
    sessionStorage.setItem(
      SESSION_STEP_KEY,
      JSON.stringify({ userId: String(userId || ''), stepIndex: index }),
    );
  } catch (_e) {
    // ignore
  }
}

export function clearSessionStepIndex() {
  try {
    sessionStorage.removeItem(SESSION_STEP_KEY);
  } catch (_e) {
    // ignore
  }
}

export default {
  TUTORIAL_VERSION,
  isDone,
  markDone,
  reset,
  loadSessionStepIndex,
  saveSessionStepIndex,
  clearSessionStepIndex,
};
